import React from 'react';
import { Tag, Quote, Barcode, ScanLine, Badge as BadgeIcon, Package2, Sparkles, Plus, HelpCircle } from 'lucide-react';
import type { Category } from '@/modelos/productos/Producto';
import type { ProductFormData } from '@/modelos/productos/Producto';
import { generateRandomProductCode, generateRandomBarcode, type FormError } from '@/hooks/productos/useProductoFormulario';
import Tooltip from '@/contasis/components/Tooltip/Tooltip';
import { Input, Select, Button } from '@/contasis';
// ── Barcode helpers ───────────────────────────────────────────────────────────
const BARCODE_MAX_LENGTH = 14;
function normalizeBarcodeValue(value?: string | null): string {
  if (value === null || value === undefined) return '';
  return value.trim().replace(/\s+/g, '').replace(/[^\x20-\x7E]/g, '');
}

interface SharedFieldProps {
  formData: ProductFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProductFormData>>;
  errors: FormError;
}

interface FieldUiProps {
  onBlur?: () => void;
  showCheck?: boolean;
  showBlueHint?: boolean;
  renderCheck?: (className?: string) => React.ReactNode;
}

interface VisibilityProps {
  isFieldVisible: (fieldId: string) => boolean;
  isFieldRequired: (fieldId: string) => boolean;
}

export const ProductNameField: React.FC<SharedFieldProps & FieldUiProps> = ({
  formData,
  setFormData,
  errors,
  onBlur,
  showCheck,
  renderCheck
}) => {
  return (
    <Input
      id="nombre"
      label="Nombre"
      required
      value={formData.nombre}
      onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
      onBlur={onBlur}
      leftIcon={<Tag className="w-3.5 h-3.5" />}
      rightIcon={showCheck ? renderCheck?.() : undefined}
      error={errors.nombre}
      size="small"
      placeholder="Ingresa el nombre del producto"
    />
  );
};

export const ProductCodeField: React.FC<SharedFieldProps & FieldUiProps> = ({
  formData,
  setFormData,
  errors,
  onBlur,
  showCheck,
  renderCheck
}) => {
  return (
    <div className="flex gap-2 w-full items-end">
      <Input
        id="codigo"
        label="Código"
        required
        labelAction={
          <Tooltip text="Identificador interno. Debe ser único en tu catálogo." position="right">
            <button
              type="button"
              aria-label="Ayuda: Código"
              className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </Tooltip>
        }
        containerClassName="flex-1 min-w-0"
        value={formData.codigo}
        onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value }))}
        onBlur={onBlur}
        leftIcon={<Barcode className="w-3.5 h-3.5" />}
        rightIcon={showCheck ? renderCheck?.() : undefined}
        error={errors.codigo}
        size="small"
        placeholder="Código único"
        className="font-mono"
      />
      <Tooltip text="Generar código aleatorio" position="right">
        <Button
          type="button"
          variant="primary"
          size="sm"
          iconOnly
          icon={<Sparkles />}
          onClick={() => {
            setFormData(prev => ({ ...prev, codigo: generateRandomProductCode() }));
            onBlur?.();
          }}
          aria-label="Generar código aleatorio"
        />
      </Tooltip>
    </div>
  );
};

export const ProductBarcodeField: React.FC<SharedFieldProps & VisibilityProps & FieldUiProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('codigoBarras')) return null;

  const handleBarcodeChange = (value: string) => {
    const normalized = normalizeBarcodeValue(value);
    setFormData(prev => ({ ...prev, codigoBarras: normalized }));
  };

  return (
    <div className="flex gap-2 w-full items-end">
      <Input
        id="codigoBarras"
        label="Código de barras"
        required={isFieldRequired('codigoBarras')}
        labelAction={
          <Tooltip text="Opcional. EAN/UPC (8–14 dígitos). Útil para lector." position="right">
            <button
              type="button"
              aria-label="Ayuda: Código de barras"
              className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </Tooltip>
        }
        containerClassName="flex-1 min-w-0"
        value={formData.codigoBarras ?? ''}
        onChange={(e) => handleBarcodeChange(e.target.value)}
        onBlur={onBlur}
        leftIcon={<ScanLine className="w-3.5 h-3.5" />}
        rightIcon={showCheck ? renderCheck?.() : undefined}
        error={errors.codigoBarras}
        size="small"
        placeholder="8-14 dígitos"
        inputMode="numeric"
        maxLength={BARCODE_MAX_LENGTH}
        autoComplete="off"
        aria-invalid={errors.codigoBarras ? 'true' : 'false'}
        className="font-mono"
      />
      <Tooltip text="Generar código aleatorio" position="right">
        <Button
          type="button"
          variant="primary"
          size="sm"
          iconOnly
          icon={<Sparkles />}
          onClick={() => {
            setFormData(prev => ({ ...prev, codigoBarras: generateRandomBarcode() }));
            onBlur?.();
          }}
          aria-label="Generar código de barras aleatorio"
        />
      </Tooltip>
    </div>
  );
};

export const ProductAliasField: React.FC<SharedFieldProps & VisibilityProps & FieldUiProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('alias')) return null;

  return (
    <Input
      id="alias"
      label={`Alias del producto${isFieldRequired('alias') ? '' : ''}`}
      required={isFieldRequired('alias')}
      value={formData.alias}
      onChange={(e) => setFormData(prev => ({ ...prev, alias: e.target.value }))}
      onBlur={onBlur}
      leftIcon={<Quote className="w-3.5 h-3.5" />}
      rightIcon={showCheck ? renderCheck?.() : undefined}
      error={errors.alias}
      size="small"
      placeholder="Nombre alternativo"
    />
  );
};

interface CategoryFieldProps extends SharedFieldProps, VisibilityProps, FieldUiProps {
  categories: Category[];
  onOpenCategoryModal: () => void;
}

export const ProductCategoryField: React.FC<CategoryFieldProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  categories,
  onOpenCategoryModal,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('categoria')) return null;

  return (
    <div className="flex gap-2 w-full items-end">
      <Select
        id="categoria"
        label="Categoría"
        required={isFieldRequired('categoria')}
        labelAction={
          <Tooltip text="Organiza tu catálogo para búsquedas y reportes." position="right">
            <button
              type="button"
              aria-label="Ayuda: Categoría"
              className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </Tooltip>
        }
        containerClassName="flex-1 min-w-0"
        value={formData.categoria}
        onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
        onBlur={onBlur}
        rightIcon={showCheck ? renderCheck?.() : undefined}
        error={errors.categoria}
        size="small"
      >
        <option value="">Seleccionar categoría</option>
        {categories.map(cat => (
          <option key={cat.id} value={cat.nombre}>
            {cat.nombre}
          </option>
        ))}
      </Select>
      <Tooltip text="Crear categoría nueva." position="right">
        <Button
          type="button"
          variant="primary"
          size="sm"
          iconOnly
          icon={<Plus />}
          onClick={onOpenCategoryModal}
          aria-label="Crear categoría nueva"
        />
      </Tooltip>
    </div>
  );
};

export const ProductBrandField: React.FC<SharedFieldProps & VisibilityProps & FieldUiProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('marca')) return null;

  return (
    <Input
      id="marca"
      label="Marca"
      required={isFieldRequired('marca')}
      value={formData.marca}
      onChange={(e) => setFormData(prev => ({ ...prev, marca: e.target.value }))}
      onBlur={onBlur}
      leftIcon={<BadgeIcon className="w-3.5 h-3.5" />}
      rightIcon={showCheck ? renderCheck?.() : undefined}
      error={errors.marca}
      size="small"
      placeholder="Marca del producto"
    />
  );
};

export const ProductModelField: React.FC<SharedFieldProps & VisibilityProps & FieldUiProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('modelo')) return null;

  return (
    <Input
      id="modelo"
      label="Modelo"
      required={isFieldRequired('modelo')}
      value={formData.modelo}
      onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
      onBlur={onBlur}
      leftIcon={<Package2 className="w-3.5 h-3.5" />}
      rightIcon={showCheck ? renderCheck?.() : undefined}
      error={errors.modelo}
      size="small"
      placeholder="Modelo del producto"
    />
  );
};


